import {Result} from "neverthrow"
import React, {createContext, PropsWithChildren, useContext, useMemo} from "react"
import {useAsyncExpiredMemo} from "../hooks/useAsyncExpiredMemo"
import {getOrgCustomerToken} from "../resources/org"
import {ErrorDocument} from "../resources/common"
import {useAccessToken, useIsBankUser, useIsOrgUser, useIsUnitUser} from "./auth"
import {assertIsDefined} from "../utilities/assert"
import Loading from "../components/Loading/Loading"
import {Err} from "../components/Err/Err"
import {getByOrg, getByToken} from "../resources/orgSettings"
import {useAsyncResult} from "../hooks/useAsyncResult"

const context = createContext<OrgCustomerToken | undefined>(undefined)

export type OrgCustomerToken = {
	type: "orgCustomerToken"
	id: string
	attributes: {
		token: string
		expiresIn: number
		customerId: string
	}
}

function getCustomerToken(accessToken: string): Promise<Result<OrgCustomerToken, ErrorDocument>> {
	return getOrgCustomerToken(accessToken)
}

export function OrgAuthProvider({
	children,
	onError,
}: PropsWithChildren<{
	onError?: (e: ErrorDocument) => JSX.Element
}>) {
	const accessToken = useAccessToken()
	const customerToken = useAsyncExpiredMemo(
		() => getCustomerToken(accessToken),
		(t) => t.attributes.expiresIn
	)

	return customerToken.match(
		() => <Loading />,
		(t) => <context.Provider value={t}>{children}</context.Provider>,
		(err) => (onError ? onError(err) : <Err err={err} />)
	)
}

export function useCustomerAccessToken() {
	const token = useContext(context)
	const accessToken = token?.attributes.token
	assertIsDefined(accessToken)
	return accessToken
}

export function getOrgTokenCustomer() {
	const token = useContext(context)
	const customerId = token?.attributes.customerId
	assertIsDefined(customerId)
	return customerId
}

export function useCustomerToken() {
	const token = useContext(context)
	assertIsDefined(token)
	return token
}

export function isOrg() {
	return !!useContext(context)
}

export function useOrgSettings(orgId: string) {
	const accessToken = useAccessToken()
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const isOrgUser = useIsOrgUser()
	// Memoize the fetch function to prevent re-creation on every render
	const fetchFunction = useMemo(() => {
		if (isUnitUser || isBankUser) {
			return () => getByOrg(accessToken, orgId)
		} else if (isOrgUser) {
			return () => getByToken(accessToken)
		}
		return () => getByToken(accessToken)
	}, [accessToken, orgId, isUnitUser, isBankUser, isOrgUser])

	return useAsyncResult(fetchFunction, [accessToken, orgId, isUnitUser, isBankUser, isOrgUser])
}
